import { type Href, Link, type LinkProps } from 'expo-router';
import type { ReactNode } from 'react';
import { Pressable, View } from 'react-native';
import {
    Button as SmoothieButton,
    type ButtonSize,
    type ButtonVariant,
    Icon,
    type IconType,
    Spinner,
    Text,
    YStack,
} from 'smoothie';

interface LinkableComponentProps extends Omit<LinkProps, 'href'> {
    href?: Href;
}

export const LinkableComponent = ({
    children,
    href,
    disabled,
    onPress,
    ...props
}: LinkableComponentProps) => {
    if (href !== undefined) {
        return (
            <Link href={href} {...props}>
                {children}
            </Link>
        );
    }

    return (
        <Pressable onPress={onPress} disabled={disabled}>
            {children}
        </Pressable>
    );
};

type GroveButtonVariant = 'primary' | 'secondary' | 'tertiary';

interface GroveButtonProps extends Omit<LinkProps, 'children' | 'href'> {
    href?: Href;
    children?: string;
    variant?: GroveButtonVariant;
    size?: 'tiny' | 'small' | 'large';
    width?: 'auto' | 'full';
    tone?: 'neutral' | 'positive' | 'danger';
    icon?: IconType | ReactNode;
    iconPosition?: 'left' | 'right';
    disabled?: boolean;
}

// We could use Smoothie's Button everywhere, but it's current interface requires a decent
// amount of repetitive/copied props for nearly all uses in Grove. This wrapper translates
// a simplified, defaults-included interface to the Smoothie Button interface.
// If this works well, I'd like to update the Smoothie Button to use this interface.
export const Button = ({
    children = '', // TODO defaulting to '' is a workaround until smoothie's buttons support links like Grove's Button
    variant = 'primary',
    size = 'large',
    width = 'auto',
    tone = 'neutral',
    icon,
    iconPosition,
    disabled = false,
    ...linkableComponentProps
}: GroveButtonProps) => {
    const smoothieVariant = variant as ButtonVariant;

    return (
        <LinkableComponent disabled={disabled} {...linkableComponentProps}>
            <SmoothieButton
                text={children}
                variant={smoothieVariant}
                size={size as ButtonSize.LARGE | ButtonSize.SMALL}
                tone={tone}
                width={width}
                disabled={disabled}
                icon={icon}
                iconPosition={iconPosition}
                {...(variant !== 'tertiary' && { paddingHorizontal: 24 })}
            />
        </LinkableComponent>
    );
};

interface GenericButtonProps extends Omit<LinkProps, 'children' | 'href'> {
    href?: Href;
    icon: IconType;
    label: string;
    isWaiting?: boolean;
    iconRotation?: string;
}

export const SmallTileButton = ({
    icon,
    label,
    isWaiting,
    iconRotation,
    ...linkableComponentProps
}: GenericButtonProps) => {
    return (
        <LinkableComponent {...linkableComponentProps}>
            <YStack
                justifyContent="center"
                alignItems="center"
                gap={2}
                borderRadius={8}
                paddingVertical={8}
                paddingHorizontal={10}
                minWidth={60}
                hoverStyle={{ backgroundColor: '$dropletBlue100' }}
                userSelect="none"
            >
                {isWaiting ? (
                    <Spinner />
                ) : (
                    <>
                        <View
                            style={
                                iconRotation != null && iconRotation !== ''
                                    ? { transform: [{ rotate: iconRotation }] }
                                    : undefined
                            }
                        >
                            <Icon icon={icon} size={18} />
                        </View>
                        <Text fontSize={10}>{label}</Text>
                    </>
                )}
            </YStack>
        </LinkableComponent>
    );
};
